import React from 'react';
import { COLORS, FIELD_NAME, TOP_INDENTS } from '@constants';
import { PLATFORM_ID } from '@enums';
import { Typography } from '@ui';
import { Col, Form, Radio, Row } from 'antd';
import { UNAVAILABLE_REASON, UNAVAILABLE_TOOLTIP } from '../../constants';
import CampaignTypeCard from '../CampaignTypeCard';
import CardExpandedContent from '../CardExpandedContent';

const platformDescription = (platformId) => {
    switch (platformId) {
        case PLATFORM_ID.BIGCOMMERCE:
            return `Pick the type of products you'd like to advertise. We recommend choosing your 'Entire
                    catalog', but you can also select 'Best-sellers' or 'Specific products'.
                    For each of these campaign types, Klickly will automatically pull your products into an ad campaign
                    and keep this campaign up-to-date based on changes to your catalog, best-sellers, etc. And
                    don't worry - you can review your choices on the next page.`;
        default:
            return `Pick the type of products you'd like to advertise. We recommend choosing your 'Entire
                    catalog', but you can also select 'Best-sellers', 'Specific collections',
                    'Specific product types', or 'Specific products'. For each of these campaign
                    types, Klickly will automatically pull your products into an ad campaign and keep this campaign
                    up-to-date based on changes to your catalog, best-sellers, collections, etc. And don't worry -
                    you can review your choices on the next page.`;
    }
};

const CampaignTypeCards = ({ limitedCampaigns, platformId, campaignTypes = [], selectedType, currentCatalogType }) => {
    // The type of the campaign being edited stays selectable even if its data
    // went missing - otherwise the pre-filled radio would land on a disabled card.
    const getDisabledState = ({ value, unavailableReason }) => {
        if (value === currentCatalogType) return { isDisabled: false };

        const limitedCampaign = limitedCampaigns.find((campaign) => campaign.catalogType === value);

        if (limitedCampaign) {
            return {
                isDisabled: true,
                tooltip: {
                    title: `Cannot create ${limitedCampaign.title} campaign`,
                    text: `You already have a ${limitedCampaign.title} campaign. Try creating another campaign type.`
                }
            };
        }

        if (unavailableReason) {
            return { isDisabled: true, tooltip: UNAVAILABLE_TOOLTIP[value]?.[unavailableReason] };
        }

        return { isDisabled: false };
    };

    // An `empty` type has nothing to advertise and no ETA to offer, so it is dropped from the grid
    // entirely - only `syncing` earns a disabled block. The type being edited always stays.
    const visibleTypes = campaignTypes.filter(
        ({ value, unavailableReason }) => unavailableReason !== UNAVAILABLE_REASON.EMPTY || value === currentCatalogType
    );

    return (
        <React.Fragment>
            <Col style={{ display: 'flex', alignItems: 'center', color: COLORS.$gray60Black }}>
                <Typography.Text color={COLORS.$gray60Black} data-e2e='campaignwizard_step-1_description'>
                    {platformDescription(platformId)}
                </Typography.Text>
            </Col>
            <Form.Item
                style={TOP_INDENTS.M}
                name={FIELD_NAME.CAMPAIGN.TYPE}
                rules={[{ required: true, message: 'Please, choose one of campaign types' }]}
            >
                <Radio.Group>
                    <Row gutter={[16, 16]}>
                        {visibleTypes.map(
                            ({ expandable, expandedContentProps, unavailableReason, ...campaignTypeProps }) => {
                                return (
                                    <Col span={12} key={campaignTypeProps.label}>
                                        <CampaignTypeCard
                                            {...getDisabledState({ ...campaignTypeProps, unavailableReason })}
                                            {...campaignTypeProps}
                                            expandedContent={
                                                expandable && <CardExpandedContent {...expandedContentProps} />
                                            }
                                            expanded={selectedType === campaignTypeProps.value}
                                        />
                                    </Col>
                                );
                            }
                        )}
                    </Row>
                </Radio.Group>
            </Form.Item>
        </React.Fragment>
    );
};

export default CampaignTypeCards;
